import { useEffect, useRef } from "react";
import { useSessionStorageState } from "../utils/storage/storageUtils";

interface PaginationOptions {
  defaultPage?: number;
  defaultLimit?: number;
  resetOn?: unknown[]; // page -> defaultPage when any value changes (after mount)
  totalPages?: number; // optional: clamp a stale persisted page into range
}

// Persists a table's page/limit in the shared `vf:session` store under `keyPrefix`,
// so they survive navigation within the session. Pass `resetOn` to reset back to the
// first page when filters change (skipping the initial mount, so a restored page is
// kept). Sort is persisted separately with useSessionStorageState, since sort shapes
// vary per table.
export function usePersistedPagination(
  keyPrefix: string,
  { defaultPage = 1, defaultLimit = 10, resetOn, totalPages }: PaginationOptions = {}
) {
  const [page, setPage] = useSessionStorageState(
    `${keyPrefix}:page`,
    defaultPage
  );
  const [limit, setLimit] = useSessionStorageState(
    `${keyPrefix}:limit`,
    defaultLimit
  );

  // Reset to first page on filter change, but NOT on initial mount, so a
  // restored page survives navigation.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(defaultPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetOn ?? []);

  // Clamp a stale persisted page once totals are known (e.g. rows deleted).
  useEffect(() => {
    if (totalPages && totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [totalPages, page, setPage]);

  return { page, setPage, limit, setLimit };
}
